'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Step1EntityDetails from './Step1EntityDetails';
import Step2AnalysisConfig from './Step2AnalysisConfig';
import ReviewPanel from './ReviewPanel';
import { toast } from 'sonner';
import { createRun } from '@/lib/api';

export type EntityFormData = {
  // Step 1
  name: string;
  type: 'Brand' | 'Individual' | 'Organization' | '';
  description: string;
  website: string;
  handles: {
    twitter: string;
    linkedin: string;
    instagram: string;
    facebook: string;
    tiktok: string;
    reddit: string;
    youtube: string;
  };
  // Step 2
  dateFrom: string;
  dateTo: string;
  sources: {
    news: boolean;
    social: boolean;
    web: boolean;
  };
  analysisDepth: 'standard' | 'deep' |'comprehensive';
  tags: string;
  frequency: 'once' | 'daily' | 'weekly';
};

const steps = [
  { id: 1, label: 'Entity Details', icon: 'BuildingOffice2Icon' },
  { id: 2, label: 'Analysis Config', icon: 'AdjustmentsHorizontalIcon' },
  { id: 3, label: 'Review & Launch', icon: 'RocketLaunchIcon' },
];

const defaultFormData: EntityFormData = {
  name: '',
  type: '',
  description: '',
  website: '',
  handles: {
    twitter: '',
    linkedin: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    reddit: '',
    youtube: '',
  },
  dateFrom: '2026-04-01',
  dateTo: '2026-05-26',
  sources: { news: true, social: true, web: true },
  analysisDepth: 'standard',
  tags: '',
  frequency: 'once',
};

export default function CreateEditEntityContent() {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<EntityFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = false; // Backend: derive from URL params or passed prop

  const updateFormData = (partial: Partial<EntityFormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  const handleNext = () => setStep((s) => Math.min(s + 1, 3));
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Build data_sources from toggles
      const data_sources: string[] = [];
      if (formData.sources.web) data_sources.push('tavily');
      if (formData.sources.news) data_sources.push('newsapi');
      if (formData.sources.social) data_sources.push('data365');
      if (data_sources.length === 0) data_sources.push('tavily');

      // Collect only the platforms the backend supports
      const SUPPORTED_PLATFORMS = ['twitter', 'linkedin', 'instagram', 'facebook'] as const;
      const social_handles: Record<string, string> = {};
      for (const p of SUPPORTED_PLATFORMS) {
        const h = formData.handles[p]?.trim();
        if (h) social_handles[p] = h;
      }

      // data365 requires at least one handle; fall back to tavily-only
      if (data_sources.includes('data365') && Object.keys(social_handles).length === 0) {
        data_sources.splice(data_sources.indexOf('data365'), 1);
        if (data_sources.length === 0) data_sources.push('tavily');
      }

      const tags = formData.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const { run_id } = await createRun({
        entity: formData.name,
        context_intent: formData.description || `Reputation analysis for ${formData.name}`,
        data_sources,
        social_handles,
        run_mode: 'full',
        entity_type: formData.type || undefined,
        website: formData.website || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });

      toast.success(`${formData.name} added to monitoring`, {
        description: 'Analysis has been queued and will complete within 2–5 minutes.',
      });
      router.push(`/entity-profile?run_id=${run_id}`);
    } catch (err) {
      toast.error('Failed to start analysis', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:135:4" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="135" data-component-end-line="213" data-component-file="CreateEditEntityContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-screen-2xl%20mx-auto%20px-6%20lg%3Apx-8%20xl%3Apx-10%202xl%3Apx-16%20py-8%22%7D" className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Page Header */}
      <div data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:137:6" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="137" data-component-end-line="145" data-component-file="CreateEditEntityContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
        <h1 data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:138:8" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="138" data-component-end-line="140" data-component-file="CreateEditEntityContent.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-foreground%20tracking-tight%22%7D" className="text-2xl font-bold text-foreground tracking-tight">
          {isEditMode ? 'Edit Entity' : 'Add New Entity'}
        </h1>
        <p data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:141:8" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="141" data-component-end-line="144" data-component-file="CreateEditEntityContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-1%22%7D" className="text-sm text-muted-foreground mt-1">
          {isEditMode
            ? 'Update entity details and analysis configuration' :'Configure a new entity for reputation monitoring across news, social, and web sources'}
        </p>
      </div>

      {/* Step Progress */}
      <div data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:148:6" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="148" data-component-end-line="185" data-component-file="CreateEditEntityContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-0%20mb-10%22%7D" className="flex items-center gap-0 mb-10">
        {steps.map((s, i) => (
          <React.Fragment key={`step-${s.id}`}>
            <button data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:151:12" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="151" data-component-end-line="175" data-component-file="CreateEditEntityContent.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              onClick={() => step > s.id && setStep(s.id)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                step === s.id
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : step > s.id
                  ? 'text-positive cursor-pointer hover:bg-positive/10' :'text-muted-foreground cursor-default'
              }`}
            >
              <div data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:160:14" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="160" data-component-end-line="173" data-component-file="CreateEditEntityContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step > s.id
                    ? 'bg-positive text-white'
                    : step === s.id
                    ? 'bg-primary text-white' :'bg-muted text-muted-foreground'
                }`}
              >
                {step > s.id ? (
                  <Icon data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:169:18" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="169" data-component-end-line="169" data-component-file="CreateEditEntityContent.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={12} />
                ) : (
                  s.id
                )}
              </div>
              <span data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:174:14" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="174" data-component-end-line="174" data-component-file="CreateEditEntityContent.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20sm%3Ablock%22%7D" className="hidden sm:block">{s.label}</span>
            </button>
            {i < steps.length - 1 && (
              <div data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:177:14" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="177" data-component-end-line="181" data-component-file="CreateEditEntityContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`flex-1 h-px mx-2 transition-colors duration-300 ${
                  step > s.id ? 'bg-positive/40' : 'bg-border'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:188:6" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="188" data-component-end-line="212" data-component-file="CreateEditEntityContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fade-in%22%7D" className="fade-in">
        {step === 1 && (
          <Step1EntityDetails data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:190:10" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="190" data-component-end-line="194" data-component-file="CreateEditEntityContent.tsx" data-component-name="Step1EntityDetails" data-component-content="%7B%22elementName%22%3A%22Step1EntityDetails%22%7D"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
          />
        )}
        {step === 2 && (
          <Step2AnalysisConfig data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:197:10" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="197" data-component-end-line="202" data-component-file="CreateEditEntityContent.tsx" data-component-name="Step2AnalysisConfig" data-component-content="%7B%22elementName%22%3A%22Step2AnalysisConfig%22%7D"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}
        {step === 3 && (
          <ReviewPanel data-component-id="src/app/create-edit-entity/components/CreateEditEntityContent.tsx:205:10" data-component-path="src/app/create-edit-entity/components/CreateEditEntityContent.tsx" data-component-line="205" data-component-end-line="210" data-component-file="CreateEditEntityContent.tsx" data-component-name="ReviewPanel" data-component-content="%7B%22elementName%22%3A%22ReviewPanel%22%7D"
            formData={formData}
            onBack={handleBack}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
}